import * as React from 'react';
import { Activity } from 'models/Activity';
import {
    createErrorMessage,
    fieldErrorCodes,
    requiredFieldErrorMessage,
    createGlobalErrorFromAPIError,
    createFieldErrorFromAPIError,
} from 'shared/error-messages';
import TextField from 'components/forms/TextField';
import { ErrorMessage, FormCard } from 'components/forms/FormComponents';
import { FlexMd, Headline2 } from 'components/Elements';
import useModifyRequest from 'hooks/useModifyRequest';
import strings from 'strings/strings.json';
import { APIError } from 'lib/_api-helpers';
import useNeveForm from 'components/forms/NeveForm';
import { BuildCourseContext } from 'contextProviders/BuildCourseContext';
import { validateThreeCharacterLimitDurationRequired } from 'lib/custom-form-validation';
import AccessibilityNotifier from 'components/notifiers/AccessibilityNotifier';
import useFormBlocker from 'components/forms/useFormBlocker';
import { Course } from 'models/Course';
import {
    EditModeSaveNotification,
    LastActivityInModuleWarning,
    ModuleOnlyHasOneTask,
    SavedSuccessfullyNotification,
} from './activityForms/editActivityForms/shared/SharedEditTaskComponents';
import DeleteActivityAlert from './activityForms/editActivityForms/shared/DeleteActivityAlert';
import styled from 'styled-components';
import { breakpoints } from 'theme';
import { ModuleType } from 'models/Module';
import Button from 'components/buttons/Button';

type LiveTrainingUpdateInput = {
    title: string;
    trainingDuration: number;
};

type LiveTrainingUpdateFormProps = {
    activity: Activity;
    returnToCourseStructure: () => void;
};

export default function LiveTrainingUpdateForm({
    activity,
    returnToCourseStructure,
}: LiveTrainingUpdateFormProps): JSX.Element {
    const course = React.useContext(BuildCourseContext).course!;
    const { setCourseState, editMode, refetchCourse, setEditActivityId } = React.useContext(BuildCourseContext)!;

    React.useEffect(() => {
        return () => {
            setEditActivityId('');
        };
    }, []); //eslint-disable-line

    const {
        register,
        handleSubmit,
        setError,
        watch,
        formState: { errors, isDirty },
        reset,
    } = useNeveForm<LiveTrainingUpdateInput>({
        title: activity?.title ?? '',
        trainingDuration: activity?.durationInMins ?? 0,
    });

    useFormBlocker(isDirty, strings.courseEditorPage.unsavedChangesPrompt);

    const [globalErrorMessage, setGlobalErrorMessage] = React.useState<string>('');
    const [saved, setSaved] = React.useState<boolean>(false);
    const [editModeSaved, setEditModeSaved] = React.useState<boolean>(false);
    const [alertOpen, setAlertOpen] = React.useState(false);

    const trainingDuration = watch('trainingDuration');

    const titleInputProps = register('title', {
        required: {
            value: true,
            message: requiredFieldErrorMessage(strings.activityDetailsForm.titleInputLabel),
        },
        maxLength: {
            value: 120,
            message: createErrorMessage(strings.activityDetailsForm.titleInputLabel, fieldErrorCodes.maxLength),
        },
        onChange: () => saved && setSaved(false),
    });

    const durationInputProps = register('trainingDuration', {
        required: {
            value: true,
            message: requiredFieldErrorMessage(strings.liveTrainingDetailsForm.durationInputLabel),
        },
        validate: {
            validateThreeCharacterLimitDurationRequired,
        },
        onChange: () => saved && setSaved(false),
    });

    function updateErrors(apiErrors: APIError[]): void {
        const fieldMap: Record<string, string> = {
            title: 'title',
            duration: 'duration',
        };

        apiErrors.forEach((apiError) => {
            const { field, code } = apiError;

            if (field === 'global') {
                return setGlobalErrorMessage(createGlobalErrorFromAPIError(code));
            }
            setError(
                fieldMap[field] as keyof LiveTrainingUpdateInput,
                createFieldErrorFromAPIError(fieldMap[field], code),
            );
        });
    }

    const { modifyData: updateLiveTraining, loading: createLoading } = useModifyRequest(
        `activities/live-training/${activity.id}`,
        'PUT',
    );

    async function onSubmit(formData: LiveTrainingUpdateInput) {
        const updateLiveTrainingRequest = {
            title: formData.title,
            duration: formData.trainingDuration,
        };

        if (editMode) {
            setCourseState((prev) => {
                const copy = structuredClone(prev) as Course;
                const mod = copy.modules.find((m) => m.id === activity.moduleId);
                if (mod) {
                    const act = mod.activities.find((a) => a.id === activity.id);
                    if (act) {
                        act.title = formData.title;
                        act.durationInMins = formData.trainingDuration;
                    }
                }
                return copy;
            });

            reset(formData);
            setEditModeSaved(true);
            return;
        }

        const response = await updateLiveTraining(updateLiveTrainingRequest);

        const { errors } = response;

        if (errors) {
            updateErrors(errors);
            return;
        }

        setGlobalErrorMessage('');
        setSaved(true);
        reset(formData);

        await refetchCourse();
    }

    const moduleOnlyHasOneTask = ModuleOnlyHasOneTask(course, activity.moduleId);
    const moduleType = course.modules.find((m) => m.id === activity.moduleId)!.moduleType;

    return (
        <FormCard onSubmit={handleSubmit(onSubmit)} id="activityForm">
            <Headline2>{strings.updateTrainingDetailsForm.instructionEdit}</Headline2>
            <TextField
                fieldName="activityTitle"
                labelText={strings.updateTrainingDetailsForm.titleInputLabel}
                inputAria={strings.updateTrainingDetailsForm.titleInputAriaLabel}
                errorMessage={errors.title?.message}
                required
                autoFocus
                {...titleInputProps}
            />
            <TextField
                fieldName="activityTrainingDuration"
                labelText={strings.updateTrainingDetailsForm.durationInputLabel}
                inputAria={strings.updateTrainingDetailsForm.durationInputAria}
                errorMessage={errors.trainingDuration?.message}
                type="number"
                width="8rem"
                unitText={strings.updateTrainingDetailsForm.durationInputMins}
                required
                {...durationInputProps}
            />
            {trainingDuration > 45 && <AccessibilityNotifier accessibilityGuideKey={'liveTrainingTooLong'} />}
            {globalErrorMessage && <ErrorMessage id="errorMessage">{globalErrorMessage}</ErrorMessage>}
            {editMode && moduleOnlyHasOneTask && <LastActivityInModuleWarning />}

            <ButtonAndNotificationContainer>
                {saved && !isDirty && <SavedSuccessfullyNotification />}
                {editModeSaved && !isDirty && <EditModeSaveNotification />}
                {moduleType === ModuleType.LEARNER_LED && (
                    <Button
                        title={strings.courseContentPage.deleteActivityButton}
                        aria={strings.courseContentPage.deleteActivityButtonAria}
                        colour={'BLACK'}
                        onClick={() => setAlertOpen(true)}
                        disabled={editMode && moduleOnlyHasOneTask}
                    />
                )}
                <Button
                    type="submit"
                    title={strings.updateTrainingDetailsForm.submitButton}
                    aria={strings.updateTrainingDetailsForm.submitButtonAria}
                    disabled={createLoading || (editMode && !isDirty)}
                />
            </ButtonAndNotificationContainer>

            {alertOpen && (
                <DeleteActivityAlert
                    taskId={activity.id}
                    moduleId={module.id}
                    returnToCourseStructure={returnToCourseStructure}
                    setAlertOpen={setAlertOpen}
                />
            )}
        </FormCard>
    );
}

const ButtonAndNotificationContainer = styled(FlexMd)`
    justify-content: flex-end;
    align-items: center;
    width: 100%;
    min-height: 72px;
    @media (max-width: ${breakpoints.md}) {
        flex-direction: column-reverse;
        align-items: unset;
    }
`;
